import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Trash2, Upload, LogOut } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";

const EMPTY_GEN = { name: "", capacity: "", price: "", tag: "", image: "", description: "" };

export default function AdminDashboard() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [queries, setQueries] = useState([]);
  const [gens, setGens] = useState([]);
  const [genForm, setGenForm] = useState(EMPTY_GEN);
  const [editing, setEditing] = useState(null);
  const [summarizing, setSummarizing] = useState("");
  const fileRef = useRef(null);

  const loadInquiries = useCallback(() => api.get("/admin/inquiries").then((r) => setInquiries(r.data)).catch(() => {}), []);
  const loadQueries = useCallback(() => api.get("/admin/queries").then((r) => setQueries(r.data)).catch(() => {}), []);
  const loadGens = useCallback(() => api.get("/generators").then((r) => setGens(r.data)).catch(() => {}), []);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { localStorage.removeItem("kgs_token"); nav("/admin"); });
  }, [nav]);
  useEffect(() => { if (user) { loadInquiries(); loadQueries(); loadGens(); } }, [user, loadInquiries, loadQueries, loadGens]);

  const logout = () => { localStorage.removeItem("kgs_token"); nav("/admin"); };

  const summarize = async (id) => {
    setSummarizing(id);
    try {
      const { data } = await api.post(`/admin/inquiries/${id}/summarize`);
      setInquiries((list) => list.map((i) => (i.inquiry_id === id ? { ...i, ai_summary: data.summary } : i)));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSummarizing(""); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      setGenForm((f) => ({ ...f, image: data.url }));
      toast.success("Photo uploaded.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveGen = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/generators/${editing}`, genForm);
      else await api.post("/admin/generators", genForm);
      toast.success(editing ? "Generator updated." : "Generator added.");
      setGenForm(EMPTY_GEN); setEditing(null); loadGens();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeGen = async (id) => {
    try { await api.delete(`/admin/generators/${id}`); toast.success("Removed."); loadGens(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (!user) return <div className="adm-loading" data-testid="admin-loading">AUTHENTICATING…</div>;

  return (
    <div className="adm">
      <header className="adm-top">
        <a className="brand" href="/"><Logo size={30} /><span className="brand-name"><strong>KRISHNA</strong><small>ADMIN TERMINAL</small></span></a>
        <div className="adm-user">
          <span data-testid="admin-user-email">{user.email}</span>
          <button data-testid="admin-logout-button" className="btn btn-ghost" onClick={logout}><LogOut size={14} /> Logout</button>
        </div>
      </header>
      <div className="adm-tabs">
        <button data-testid="tab-inquiries" className={tab === "inquiries" ? "adm-tab on" : "adm-tab"} onClick={() => setTab("inquiries")}>INQUIRIES ({inquiries.length})</button>
        <button data-testid="tab-queries" className={tab === "queries" ? "adm-tab on" : "adm-tab"} onClick={() => setTab("queries")}>QUERIES ({queries.length})</button>
        <button data-testid="tab-generators" className={tab === "generators" ? "adm-tab on" : "adm-tab"} onClick={() => setTab("generators")}>FLEET ({gens.length})</button>
      </div>

      {tab === "inquiries" && (
        <div className="adm-panel" data-testid="inquiries-panel">
          {inquiries.length === 0 && <p className="adm-empty">No inquiries yet. New bookings from the website appear here.</p>}
          {inquiries.map((i) => (
            <article className="inq-row" key={i.inquiry_id} data-testid={`inquiry-row-${i.inquiry_id}`}>
              <div className="inq-main">
                <div className="inq-head">
                  <strong>{i.name}</strong>
                  <span className={i.advance_status === "paid" ? "chip paid" : "chip"}>{(i.advance_status || "unpaid").toUpperCase()}</span>
                  <span className="chip amber">{i.required_capacity}</span>
                </div>
                <p>{i.phone} · {i.email}{i.company ? ` · ${i.company}` : ""}</p>
                <p>{i.site_address} — starts {i.start_date}, {i.rental_duration}{i.notes ? ` · “${i.notes}”` : ""}</p>
                <small>{new Date(i.created_at).toLocaleString("en-IN")}</small>
              </div>
              <div className="inq-side">
                <button data-testid={`summarize-${i.inquiry_id}`} className="btn btn-ghost sm" onClick={() => summarize(i.inquiry_id)} disabled={summarizing === i.inquiry_id}>
                  <Sparkles size={13} /> {summarizing === i.inquiry_id ? "Thinking…" : i.ai_summary ? "Re-summarize" : "AI summary"}
                </button>
                {i.ai_summary && <p className="inq-summary" data-testid={`summary-${i.inquiry_id}`}>{i.ai_summary}</p>}
              </div>
            </article>
          ))}
        </div>
      )}

      {tab === "queries" && (
        <div className="adm-panel" data-testid="queries-panel">
          {queries.length === 0 && <p className="adm-empty">No queries yet. Questions from the website appear here.</p>}
          {queries.map((q) => (
            <article className="inq-row q-row" key={q.query_id} data-testid={`query-row-${q.query_id}`}>
              <div className="inq-main">
                <div className="inq-head"><strong>{q.name}</strong><span className="chip">QUERY</span></div>
                <p>{q.phone}</p>
                <p>“{q.message}”</p>
                <small>{new Date(q.created_at).toLocaleString("en-IN")}</small>
              </div>
            </article>
          ))}
        </div>
      )}

      {tab === "generators" && (
        <div className="adm-panel" data-testid="generators-panel">
          <form className="gen-form" data-testid="generator-form" onSubmit={saveGen}>
            <p className="f-step"><span>{editing ? "EDIT" : "ADD"}</span> DG SET</p>
            <div className="f-grid">
              <label className="f-label">Name *<input data-testid="gen-name-input" required value={genForm.name} onChange={(e) => setGenForm({ ...genForm, name: e.target.value })} placeholder="KGS SCOUT-15" /></label>
              <label className="f-label">Capacity *<input data-testid="gen-capacity-input" required value={genForm.capacity} onChange={(e) => setGenForm({ ...genForm, capacity: e.target.value })} placeholder="15 kVA" /></label>
              <label className="f-label">Price *<input data-testid="gen-price-input" required value={genForm.price} onChange={(e) => setGenForm({ ...genForm, price: e.target.value })} placeholder="₹ 2,800 / day" /></label>
              <label className="f-label">Tag<input data-testid="gen-tag-input" value={genForm.tag} onChange={(e) => setGenForm({ ...genForm, tag: e.target.value })} placeholder="QUICK DISPATCH" /></label>
              <label className="f-label wide">Description<input data-testid="gen-desc-input" value={genForm.description} onChange={(e) => setGenForm({ ...genForm, description: e.target.value })} placeholder="Best for…" /></label>
            </div>
            <div className="gen-upload">
              <button data-testid="gen-upload-button" type="button" className="btn btn-ghost sm" onClick={() => fileRef.current?.click()}><Upload size={14} /> Upload photo</button>
              <input ref={fileRef} data-testid="gen-file-input" type="file" accept="image/*" hidden onChange={uploadImage} />
              {genForm.image && <span className="gen-img-ok" data-testid="gen-image-ready">Photo ready ✓</span>}
              <button data-testid="gen-save-button" className="btn btn-amber sm" type="submit">{editing ? "Save changes" : "Add generator"}</button>
              {editing && <button type="button" className="btn btn-ghost sm" onClick={() => { setEditing(null); setGenForm(EMPTY_GEN); }}>Cancel</button>}
            </div>
          </form>
          {gens.map((g) => (
            <article className="gen-row" key={g.gen_id} data-testid={`gen-row-${g.gen_id}`}>
              <div><strong>{g.name}</strong> <span className="chip amber">{g.capacity}</span><p>{g.price}{g.tag ? ` · ${g.tag}` : ""}</p></div>
              <div className="gen-actions">
                <button data-testid={`gen-edit-${g.gen_id}`} className="btn btn-ghost sm" onClick={() => { setEditing(g.gen_id); setGenForm({ name: g.name, capacity: g.capacity, price: g.price, tag: g.tag, image: g.image, description: g.description }); }}>Edit</button>
                <button data-testid={`gen-delete-${g.gen_id}`} className="btn btn-ghost sm danger" onClick={() => removeGen(g.gen_id)}><Trash2 size={13} /></button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
